import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, Cell, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer } from 'recharts'
import { relatoriosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState, LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { cn, formatDate, formatMoney } from '../../lib/utils'
import type { ComparativoLinha, ComparativoMes } from '../../types'

type Metrica = 'resultado' | 'receitas' | 'despesas'

const METRICAS: { key: Metrica; label: string }[] = [
  { key: 'resultado', label: 'Resultado' },
  { key: 'receitas', label: 'Receitas' },
  { key: 'despesas', label: 'Despesas' },
]

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const MESES_CURTOS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

const LARGURA_POR_MES = 74
const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }
const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
// Eixo enxuto: 500, 1k, 1,5k... (arredondar para inteiro repetiria "1k" em passos de 250).
const kFormatter = (v: number | string) => {
  const n = Number(v)
  if (Math.abs(n) < 1000) return String(n)
  return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`
}

/** Valor da barra: o realizado até o mês atual, e o previsto nos meses que ainda não chegaram. */
function valorDaBarra(m: ComparativoMes, metrica: Metrica): number {
  const previsto = m.futuro
  if (metrica === 'receitas') return previsto ? m.receitasPrevisto : m.receitasRealizado
  if (metrica === 'despesas') return previsto ? m.despesasPrevisto : m.despesasRealizado
  return previsto ? m.resultadoPrevisto : m.resultadoRealizado
}

const mesmoMes = (m: { mes: number; ano: number }, o: { mes: number; ano: number }) => m.mes === o.mes && m.ano === o.ano

const nomeDoMes = (mes: number) => MESES[mes - 1].charAt(0).toUpperCase() + MESES[mes - 1].slice(1)

function dataCurta(iso: string) {
  const d = new Date(iso)
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MESES_CURTOS[d.getUTCMonth()]}`
}

/** Aba "Comparativo": meses lado a lado; tocar num mês mostra os lançamentos dele. */
export function ComparativoTab() {
  const navigate = useNavigate()
  const [metrica, setMetrica] = useState<Metrica>('resultado')
  const [escolhido, setEscolhido] = useState<{ mes: number; ano: number } | null>(null)
  const [detalhe, setDetalhe] = useState<ComparativoLinha | null>(null)
  const rolagem = useRef<HTMLDivElement>(null)

  const { data, isLoading, isError } = useQuery({ queryKey: ['relatorio-comparativo'], queryFn: () => relatoriosApi.comparativo(6, 3) })

  const meses = useMemo(() => data?.meses ?? [], [data])
  const selecionado = escolhido ?? (data ? { mes: data.mesAtual, ano: data.anoAtual } : null)
  const indice = selecionado ? meses.findIndex((m) => mesmoMes(m, selecionado)) : -1
  const mesSel = indice >= 0 ? meses[indice] : undefined

  const { data: lista, isLoading: carregandoLista, isError: erroLista } = useQuery({
    queryKey: ['relatorio-comparativo-lista', selecionado?.mes, selecionado?.ano],
    queryFn: () => relatoriosApi.comparativoLancamentos(selecionado!.mes, selecionado!.ano),
    enabled: !!selecionado,
  })

  const dadosGrafico = useMemo(
    () => meses.map((m) => ({ ...m, rotulo: `${MESES_CURTOS[m.mes - 1]} ${String(m.ano).slice(2)}`, valor: valorDaBarra(m, metrica) })),
    [meses, metrica],
  )

  // Abre com o mês atual à vista quando o gráfico é mais largo que a tela.
  useEffect(() => {
    const el = rolagem.current
    if (!el || meses.length === 0) return
    const atual = meses.findIndex((m) => m.atual)
    if (atual < 0) return
    el.scrollLeft = Math.max(0, ((atual + 0.5) / meses.length) * el.scrollWidth - el.clientWidth / 2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meses.length])

  if (isLoading) return <LoadingState />
  if (isError || !data) return <EmptyState icon="ri-error-warning-line" message="Não foi possível carregar o comparativo." />
  if (!mesSel || !selecionado) return <EmptyState icon="ri-bar-chart-2-line" message="Sem dados no período." />

  const valorSel = valorDaBarra(mesSel, metrica)
  const nomeMetrica = metrica === 'resultado' ? 'Resultado' : metrica === 'receitas' ? 'Receitas' : 'Despesas'
  const corValor = metrica === 'resultado' ? (valorSel >= 0 ? 'text-income' : 'text-expense') : metrica === 'receitas' ? 'text-income' : 'text-expense'
  const ir = (delta: number) => {
    const alvo = meses[indice + delta]
    if (alvo) setEscolhido({ mes: alvo.mes, ano: alvo.ano })
  }

  function irParaEdicao(l: ComparativoLinha) {
    setDetalhe(null)
    navigate(`/transacoes?editar=${l.id}`)
  }
  function irParaFatura(l: ComparativoLinha) {
    setDetalhe(null)
    navigate(`/cartoes?cartao=${l.cartaoId}&mes=${l.faturaMes}&ano=${l.faturaAno}`)
  }

  const itens = lista?.itens ?? []
  const atrasados = lista?.atrasados ?? []
  const totalAtrasados = atrasados.reduce((s, i) => s + (i.receita ? i.valor : -i.valor), 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex overflow-hidden rounded border border-border/70" role="group" aria-label="O que comparar">
          {METRICAS.map((m) => (
            <button
              key={m.key}
              type="button"
              aria-pressed={metrica === m.key}
              onClick={() => setMetrica(m.key)}
              className={cn('px-4 py-1.5 text-13 transition-colors', metrica === m.key ? 'bg-primary/15 font-semibold text-primary' : 'text-muted hover:bg-surface2 hover:text-white')}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="text-13 text-muted"><i className="ri-information-line" aria-hidden /> Pelo caixa: a fatura do cartão pesa quando é paga. Meses claros são previstos.</p>
      </div>

      <div ref={rolagem} className="overflow-x-auto pb-1" aria-label="Comparativo de meses">
        <div style={{ minWidth: meses.length * LARGURA_POR_MES }}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dadosGrafico} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
              <XAxis
                dataKey="rotulo"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={(p) => {
                  const m = meses[p.payload.index]
                  const ativo = !!m && mesmoMes(m, selecionado)
                  return (
                    <text
                      x={p.x} y={Number(p.y) + 12} textAnchor="middle" fontSize={11}
                      fill={ativo ? '#FFB300' : '#8B949E'} fontWeight={ativo ? 700 : 400}
                      style={{ cursor: 'pointer' }}
                      onClick={() => m && setEscolhido({ mes: m.mes, ano: m.ano })}
                    >
                      {p.payload.value}
                    </text>
                  )
                }}
              />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} width={38} />
              <ReferenceLine y={0} stroke="#2D3748" />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                contentStyle={CHART_TOOLTIP}
                formatter={(v) => [formatMoney(Number(v)), nomeMetrica]}
                labelFormatter={(_, itensTip) => {
                  const m = itensTip?.[0]?.payload as ComparativoMes | undefined
                  return m ? `${MESES[m.mes - 1]} de ${m.ano}${m.futuro ? ' · previsto' : ''}` : ''
                }}
              />
              <Bar
                dataKey="valor"
                radius={[4, 4, 0, 0]}
                maxBarSize={44}
                cursor="pointer"
                onClick={(d) => setEscolhido({ mes: (d as unknown as ComparativoMes).mes, ano: (d as unknown as ComparativoMes).ano })}
              >
                {dadosGrafico.map((m) => (
                  <Cell
                    key={`${m.ano}-${m.mes}`}
                    fill={mesmoMes(m, selecionado) ? '#FFB300' : m.futuro ? 'rgba(255,179,0,0.16)' : 'rgba(255,179,0,0.36)'}
                    stroke={m.futuro ? 'rgba(255,179,0,0.55)' : 'none'}
                    strokeDasharray={m.futuro ? '3 3' : undefined}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded border border-border/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => ir(-1)} disabled={indice <= 0} aria-label="Mês anterior" className="rounded p-1.5 text-muted hover:bg-surface2 hover:text-white disabled:opacity-30">
              <i className="ri-arrow-left-s-line text-lg" aria-hidden />
            </button>
            <h5 className="min-w-[9.5rem] text-center text-15 font-medium text-white">{nomeDoMes(mesSel.mes)} de {mesSel.ano}</h5>
            <button type="button" onClick={() => ir(1)} disabled={indice >= meses.length - 1} aria-label="Próximo mês" className="rounded p-1.5 text-muted hover:bg-surface2 hover:text-white disabled:opacity-30">
              <i className="ri-arrow-right-s-line text-lg" aria-hidden />
            </button>
          </div>
          {mesSel.futuro && <Badge variant="muted">Previsto</Badge>}
          {mesSel.atual && <Badge variant="primary">Mês atual</Badge>}
        </div>

        <p className="mt-3 text-13 text-muted">{nomeMetrica}{mesSel.futuro ? ' previsto' : mesSel.atual ? ' até agora' : ''}</p>
        <p className={cn('text-3xl font-semibold tabular-nums', corValor)}>{formatMoney(valorSel)}</p>

        <dl className="mt-3 grid gap-x-6 gap-y-1 text-13 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex justify-between gap-3"><dt className="text-muted">Receitas</dt><dd className="tabular-nums text-income">{formatMoney(mesSel.futuro ? mesSel.receitasPrevisto : mesSel.receitasRealizado)}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-muted">Despesas</dt><dd className="tabular-nums text-expense">{formatMoney(mesSel.futuro ? mesSel.despesasPrevisto : mesSel.despesasRealizado)}</dd></div>
          {mesSel.atual && (
            <div className="flex justify-between gap-3"><dt className="text-muted">Resultado previsto no fim do mês</dt><dd className={cn('tabular-nums', mesSel.resultadoPrevisto >= 0 ? 'text-income' : 'text-expense')}>{formatMoney(mesSel.resultadoPrevisto)}</dd></div>
          )}
        </dl>

        {mesSel.atrasadosQuantidade > 0 && (
          <p className="mt-3 flex items-center gap-2 text-13 text-expense">
            <i className="ri-alarm-warning-line" aria-hidden />
            {mesSel.atrasadosQuantidade} {mesSel.atrasadosQuantidade === 1 ? 'lançamento atrasado' : 'lançamentos atrasados'} neste mês, fora dos números acima.
          </p>
        )}
      </div>

      {carregandoLista ? <LoadingState /> : erroLista ? (
        <EmptyState icon="ri-error-warning-line" message="Não foi possível carregar os lançamentos do mês." />
      ) : itens.length === 0 && atrasados.length === 0 ? (
        <EmptyState icon="ri-file-list-3-line" message={`Nenhum lançamento em ${MESES[mesSel.mes - 1]} de ${mesSel.ano}.`} />
      ) : (
        <div className="space-y-6">
          {itens.length > 0 && <ListaDoMes titulo="Lançamentos do mês" linhas={itens} onAbrir={setDetalhe} />}
          {atrasados.length > 0 && (
            <ListaDoMes
              titulo="Atrasados deste mês"
              tom="atrasado"
              total={totalAtrasados}
              linhas={atrasados}
              onAbrir={setDetalhe}
            />
          )}
        </div>
      )}

      <Modal
        open={!!detalhe}
        onClose={() => setDetalhe(null)}
        title={detalhe?.tipo === 'fatura' ? 'Fatura do cartão' : 'Lançamento'}
        size="sm"
        footer={detalhe && (
          <>
            <Button variant="ghost" onClick={() => setDetalhe(null)}>Fechar</Button>
            {detalhe.tipo === 'fatura'
              ? <Button icon="ri-bank-card-line" onClick={() => irParaFatura(detalhe)}>Abrir fatura</Button>
              : <Button icon="ri-pencil-line" onClick={() => irParaEdicao(detalhe)}>Editar</Button>}
          </>
        )}
      >
        {detalhe && <DetalheLinha l={detalhe} />}
      </Modal>
    </div>
  )
}

function ListaDoMes({ titulo, linhas, onAbrir, tom, total }: { titulo: string; linhas: ComparativoLinha[]; onAbrir: (l: ComparativoLinha) => void; tom?: 'atrasado'; total?: number }) {
  const atrasado = tom === 'atrasado'
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h5 className={cn('text-15 font-medium', atrasado ? 'text-expense' : 'text-white')}>{titulo}</h5>
        {total !== undefined && <span className={cn('text-13 font-semibold tabular-nums', total >= 0 ? 'text-income' : 'text-expense')}>{formatMoney(total)}</span>}
      </div>
      <ul className={cn('divide-y divide-border/50 overflow-hidden rounded border', atrasado ? 'border-expense/40' : 'border-border/60')}>
        {linhas.map((l) => (
          <li key={`${l.tipo}-${l.id}`}>
            <button type="button" onClick={() => onAbrir(l)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface2/70">
              <span className="w-14 flex-shrink-0 text-13 tabular-nums text-muted">{dataCurta(l.data)}</span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-13 text-white">{l.descricao}</span>
                  {l.tipo === 'fatura' && <Badge variant="info"><i className="ri-bank-card-line" aria-hidden /> Fatura</Badge>}
                  {l.situacao === 'previsto' && <Badge variant="muted">Previsto</Badge>}
                </span>
                {(l.categoria || l.conta) && <span className="block truncate text-2xs text-muted">{[l.categoria, l.conta].filter(Boolean).join(' · ')}</span>}
              </span>
              <span className={cn('flex-shrink-0 text-13 font-medium tabular-nums', l.receita ? 'text-income' : 'text-white')}>
                {l.receita ? '+ ' : '− '}{formatMoney(l.valor)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function DetalheLinha({ l }: { l: ComparativoLinha }) {
  const linhas: [string, string | undefined | null][] = l.tipo === 'fatura'
    ? [
      ['Cartão', l.cartao],
      ['Vencimento', l.vencimento ? formatDate(l.vencimento) : undefined],
      ['Pagamento', l.pagamento ? formatDate(l.pagamento) : 'Ainda não paga'],
    ]
    : [
      ['Data', formatDate(l.data)],
      ['Categoria', l.categoria],
      ['Conta', l.conta],
      ['Parcela', l.parcelaNumero && l.parcelaTotal ? `${l.parcelaNumero} de ${l.parcelaTotal}` : undefined],
      ['Observação', l.observacao],
    ]
  const situacao = l.situacao === 'realizado' ? (l.tipo === 'fatura' ? 'Paga' : 'Confirmado') : l.situacao === 'previsto' ? 'Previsto' : 'Atrasado'

  return (
    <div className="space-y-4">
      <div>
        <p className="text-15 text-white">{l.descricao}</p>
        <p className={cn('text-2xl font-semibold tabular-nums', l.receita ? 'text-income' : 'text-expense')}>{l.receita ? '+ ' : '− '}{formatMoney(l.valor)}</p>
        <Badge variant={l.situacao === 'atrasado' ? 'expense' : l.situacao === 'previsto' ? 'muted' : 'income'} className="mt-2">{situacao}</Badge>
      </div>
      <dl className="space-y-2 text-13">
        {linhas.filter(([, v]) => !!v).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4"><dt className="text-muted">{k}</dt><dd className="text-right text-white">{v}</dd></div>
        ))}
      </dl>
    </div>
  )
}
